import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Card,
  Spin,
  Avatar,
  Button,
  Modal,
  Form,
  Input,
  Alert,
  Tag,
  Row,
  Col,
  Divider,
  Typography,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  UserOutlined,
  CloseOutlined,
  MailOutlined,
  EditOutlined,
  CheckCircleFilled,
  CalendarOutlined,
  SafetyCertificateOutlined,
  CameraOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";
import { AuthContext } from "../context/AuthContext";
import SingleImageUpload from "../components/SingleImageUpload";

const AdminProfile = () => {
  const { request } = useRequest();
  const { setUserProfile } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [profilePic, setProfilePic] = useState(null);
  const [savingImage, setSavingImage] = useState(false);
  const [deletingImage, setDeletingImage] = useState(false);
  const [uploadResetKey, setUploadResetKey] = useState(0);
  const [savingProfile, setSavingProfile] = useState(false);
  const [nameDirty, setNameDirty] = useState(false);
  const [initialName, setInitialName] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [verifyOtpOpen, setVerifyOtpOpen] = useState(false);
  const [emailOtpSending, setEmailOtpSending] = useState(false);
  const [emailOtpVerifying, setEmailOtpVerifying] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  const [form] = Form.useForm();
  const [changeEmailForm] = Form.useForm();
  const [verifyOtpForm] = Form.useForm();
  const nameInputRef = useRef(null);
  const formatLongDate = (dateValue) => {
    if (!dateValue) return "—";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  useEffect(() => {
    request({
      url: apiPath.authProfile,
      method: "GET",
      onSuccess: (data) => {
        const u = data?.user;
        setUser(u);
        if (u) {
          const stored = JSON.parse(localStorage.getItem("userProfile") || "{}");
          localStorage.setItem(
            "userProfile",
            JSON.stringify({
              ...stored,
              id: u._id || u.id || stored.id,
              name: u.name,
              email: u.email,
              role: u.role,
              profilePic: u.profilePic || stored.profilePic || null,
              isEmailVerified:
                typeof u.isEmailVerified === "boolean"
                  ? u.isEmailVerified
                  : stored.isEmailVerified ?? false,
            })
          );
          setUserProfile((prev) => ({
            ...prev,
            ...u,
            profilePic: u.profilePic || prev?.profilePic || null,
          }));
          setProfilePic(u.profilePic || stored.profilePic || null);
        }
        setLoading(false);
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message || err?.message || "Failed to load profile";
        ShowToast(msg, Severty.ERROR);
        setLoading(false);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync profile once on mount
  }, []);

  const cached = JSON.parse(localStorage.getItem("userProfile") || "{}");
  const display = user || cached;

  useEffect(() => {
    if (!display) return;
    const nextName = display?.name || "";
    setInitialName(nextName);
    setNameDirty(false);
    form.setFieldsValue({
      name: nextName,
      email: display?.email || "",
    });
  }, [display, form]);

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  const handleUpdateProfile = (values) => {
    const { name, email } = values || {};
    if (!name || !email) return;
    setSavingProfile(true);
    console.log("AdminProfile: updating profile", {
      url: apiPath.authUpdateProfile,
      method: "PATCH",
      data: { name, email },
    });
    request({
      url: apiPath.authUpdateProfile,
      method: "PATCH",
      data: { name, email },
      onSuccess: (data) => {
        console.log("AdminProfile: profile update success", data);
        const updated = data?.user || {};
        const stored = JSON.parse(localStorage.getItem("userProfile") || "{}");
        const merged = {
          ...stored,
          id: updated.id || stored.id,
          name: updated.name || stored.name,
          email: updated.email || stored.email,
          role: updated.role || stored.role,
          profilePic: updated.profilePic || stored.profilePic || profilePic,
          isEmailVerified:
            typeof updated.isEmailVerified === "boolean"
              ? updated.isEmailVerified
              : stored.isEmailVerified ?? false,
        };
        localStorage.setItem("userProfile", JSON.stringify(merged));
        setUserProfile(merged);
        setUser((prev) => ({
          ...(prev || {}),
          ...merged,
        }));
        setProfilePic(merged.profilePic || null);
        setInitialName(merged.name || "");
        setNameDirty(false);
        // Remove focus from name input after successful save.
        nameInputRef.current?.blur?.();
        ShowToast("Profile updated successfully", Severty.SUCCESS);
        setSavingProfile(false);
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message || err?.message || "Failed to update profile";
        ShowToast(msg, Severty.ERROR);
        setSavingProfile(false);
      },
    });
  };

  const handleOpenChangeEmail = () => {
    setChangeEmailOpen(true);
    changeEmailForm.setFieldsValue({ newEmail: "" });
  };

  const handleRequestEmailOtp = (values) => {
    const newEmail = String(values?.newEmail || "")
      .trim()
      .toLowerCase();
    if (!newEmail) return;
    setEmailOtpSending(true);
    request({
      url: apiPath.authRequestEmailChangeOtp,
      method: "POST",
      data: { newEmail },
      onSuccess: (data) => {
        setPendingEmail(data?.pendingEmail || newEmail);
        setChangeEmailOpen(false);
        setVerifyOtpOpen(true);
        setResendCountdown(Number(data?.resendCooldownSeconds) || 30);
        verifyOtpForm.resetFields();
        ShowToast(data?.message || "OTP sent to new email", Severty.SUCCESS);
        setEmailOtpSending(false);
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message || err?.message || "Failed to send OTP";
        ShowToast(msg, Severty.ERROR);
        setEmailOtpSending(false);
      },
    });
  };

  const handleVerifyEmailOtp = (values) => {
    const otp = String(values?.otp || "").trim();
    if (!otp) return;
    setEmailOtpVerifying(true);
    request({
      url: apiPath.authVerifyEmailChangeOtp,
      method: "POST",
      data: { otp },
      onSuccess: (data) => {
        const updated = data?.user || {};
        const stored = JSON.parse(localStorage.getItem("userProfile") || "{}");
        const merged = {
          ...stored,
          id: updated.id || stored.id,
          name: updated.name || stored.name,
          email: updated.email || stored.email,
          role: updated.role || stored.role,
          profilePic: updated.profilePic || stored.profilePic || null,
          isEmailVerified:
            typeof updated.isEmailVerified === "boolean"
              ? updated.isEmailVerified
              : stored.isEmailVerified,
        };
        localStorage.setItem("userProfile", JSON.stringify(merged));
        setUserProfile(merged);
        setUser((prev) => ({ ...(prev || {}), ...merged }));
        form.setFieldsValue({ email: merged.email });
        setVerifyOtpOpen(false);
        setPendingEmail("");
        setResendCountdown(0);
        ShowToast(
          data?.message || "Email verified and updated successfully",
          Severty.SUCCESS
        );
        setEmailOtpVerifying(false);
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to verify OTP";
        ShowToast(msg, Severty.ERROR);
        setEmailOtpVerifying(false);
      },
    });
  };

  const handleResendOtp = () => {
    if (!pendingEmail || resendCountdown > 0) return;
    setEmailOtpSending(true);
    request({
      url: apiPath.authRequestEmailChangeOtp,
      method: "POST",
      data: { newEmail: pendingEmail },
      onSuccess: (data) => {
        setResendCountdown(Number(data?.resendCooldownSeconds) || 30);
        ShowToast(data?.message || "OTP resent", Severty.SUCCESS);
        setEmailOtpSending(false);
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message || err?.message || "Failed to resend OTP";
        ShowToast(msg, Severty.ERROR);
        setEmailOtpSending(false);
      },
    });
  };

  const handleProfileImageUploadChange = async (uploaded) => {
    const nextUrl = uploaded?.[0]?.url;
    if (!nextUrl) return;

    setSavingImage(true);
    setProfilePic(nextUrl);

    request({
      url: apiPath.authUpdateProfileImage,
      method: "PATCH",
      data: { profilePic: nextUrl },
      onSuccess: (data) => {
        const updated = data?.user || {};
        const stored = JSON.parse(localStorage.getItem("userProfile") || "{}");
        const merged = {
          ...stored,
          profilePic: updated.profilePic || nextUrl,
          id: updated.id || stored.id,
          name: updated.name || stored.name,
          email: updated.email || stored.email,
          role: updated.role || stored.role,
        };

        localStorage.setItem("userProfile", JSON.stringify(merged));
        setUserProfile(merged);
        ShowToast("Profile image upload successfully", Severty.SUCCESS);
        setSavingImage(false);
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message || err?.message || "Failed to update profile image";
        ShowToast(msg, Severty.ERROR);
        setSavingImage(false);
      },
    });
  };

  const getS3KeyFromUrl = (url) => {
    if (!url) return null;
    try {
      const parsedUrl = new URL(url);
      const hostname = parsedUrl.hostname;
      const pathname = parsedUrl.pathname;
      let key = pathname.startsWith("/") ? pathname.substring(1) : pathname;
      if (hostname.startsWith("s3.") || hostname.includes(".s3-") || hostname.includes(".s3.")) {
        if (!hostname.startsWith("project-kitchen.")) {
          const bucketPrefix = "project-kitchen/";
          if (key.startsWith(bucketPrefix)) {
            key = key.substring(bucketPrefix.length);
          }
        }
      }
      return decodeURIComponent(key);
    } catch {
      const bucketName = "project-kitchen";
      const marker = `${bucketName}/`;
      const markerIdx = url.indexOf(marker);
      if (markerIdx !== -1) return url.substring(markerIdx + marker.length);
      const projectKitchenIdx = url.indexOf("project-kitchen/");
      if (projectKitchenIdx !== -1) return url.substring(projectKitchenIdx);
      const aleefIdx = url.indexOf("aleef/");
      if (aleefIdx !== -1) return url.substring(aleefIdx);
      return null;
    }
  };

  const handleDeleteProfileImage = async () => {
    if (!profilePic) return;
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteProfileImage = async () => {
    setDeletingImage(true);
    try {
      const key = getS3KeyFromUrl(profilePic);
      if (key) {
        await request({
          url: "upload",
          method: "DELETE",
          data: { key },
        });
      }

      request({
        url: apiPath.authDeleteProfileImage,
        method: "DELETE",
        onSuccess: (data) => {
          const stored = JSON.parse(localStorage.getItem("userProfile") || "{}");
          const merged = { ...stored, profilePic: null };
          localStorage.setItem("userProfile", JSON.stringify(merged));
          setUserProfile(merged);
          setProfilePic(null);
          setUploadResetKey((prev) => prev + 1);
          ShowToast(
            data?.message || "Profile image deleted successfully",
            Severty.SUCCESS
          );
          setDeletingImage(false);
          setDeleteConfirmOpen(false);
        },
        onError: (err) => {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            "Failed to delete profile image";
          ShowToast(msg, Severty.ERROR);
          setDeletingImage(false);
        },
      });
    } catch (err) {
      const msg = err?.message || "Failed to delete from S3";
      ShowToast(msg, Severty.ERROR);

      request({
        url: apiPath.authDeleteProfileImage,
        method: "DELETE",
        onError: () => setDeletingImage(false),
        onSuccess: () => {
          const stored = JSON.parse(localStorage.getItem("userProfile") || "{}");
          const merged = { ...stored, profilePic: null };
          localStorage.setItem("userProfile", JSON.stringify(merged));
          setUserProfile(merged);
          setProfilePic(null);
          setUploadResetKey((prev) => prev + 1);
          setDeletingImage(false);
          setDeleteConfirmOpen(false);
        },
      });
    }
  };

  const activityFeed = [
    `${display?.name || "Admin"} updated profile details`,
    `${display?.email || "admin@example.com"} account active`,
    `${display?.name || "Admin"} signed in successfully`,
  ];

  return (
    <Card title="Profile Information" bordered={false} className="criclebox">
      {loading && !display?.email ? (
        <Spin />
      ) : (
        <>
          <Row gutter={[20, 20]} justify="center">
            <Col xs={24} xl={12}>
              <Card
                bordered
                className="admin-profile-card"
              >
                <div className="admin-profile-header">
                  <div className="admin-profile-photo-card">
                    <Avatar
                      size={104}
                      src={profilePic || undefined}
                      className="admin-profile-avatar"
                      icon={<UserOutlined className="admin-profile-avatar-icon" />}
                    />
                    <div className="admin-profile-image-actions">
                      <SingleImageUpload
                        key={`profile-upload-action-${uploadResetKey}`}
                        fileType={["image/png", "image/jpg", "image/jpeg", "image/webp"]}
                        imageType="profile"
                        value={
                          profilePic
                            ? {
                              uid: "profilePic",
                              name: "profilePic",
                              status: "done",
                              url: profilePic,
                              thumbUrl: profilePic,
                            }
                            : null
                        }
                        onChange={handleProfileImageUploadChange}
                        size={5}
                        disabled={savingImage || deletingImage}
                        showUploadList={false}
                        alwaysShowButton
                        withUploadPrefix={false}
                        btnName="Upload photo"
                        buttonIcon={<CameraOutlined />}
                        buttonType="default"
                        buttonClassName="outline-button admin-profile-image-upload-btn"
                      />
                      <Button
                        type="default"
                        onClick={handleDeleteProfileImage}
                        disabled={!profilePic || deletingImage}
                        loading={deletingImage}
                        className="outline-button admin-profile-image-delete-btn"
                      >
                        <DeleteOutlined />
                      </Button>
                    </div>
                  </div>

                  <div className="admin-profile-title">
                    {display?.name || "Admin"} ({(display?.role || "admin").toUpperCase()})
                  </div>
                  <Tag
                    color={display?.isEmailVerified ? "success" : "warning"}
                    className="admin-profile-verified-tag"
                    icon={<CheckCircleFilled />}
                  >
                    {display?.isEmailVerified ? "verified" : "not verified"}
                  </Tag>
                </div>

                <Divider className="admin-profile-divider" />

                <Form form={form} layout="vertical" onFinish={handleUpdateProfile}>
                  <Form.Item
                    name="name"
                    label="Name"
                    rules={[{ required: true, message: "Please enter your name" }]}
                  >
                    <Input
                      ref={nameInputRef}
                      placeholder="Enter name"
                      className="admin-profile-name-input"
                      prefix={<UserOutlined />}
                      onChange={(e) => {
                        const nextName = String(e?.target?.value || "");
                        setNameDirty(nextName.trim() !== String(initialName || "").trim());
                      }}
                      suffix={
                        nameDirty ? (
                          <Tooltip title="Save changes">
                            <Button
                              type="primary"
                              icon={<SaveOutlined />}
                              loading={savingProfile}
                              onClick={() => form.submit()}
                              shape="circle"
                              size="small"
                              style={{
                                minWidth: 24,
                                width: 24,
                                height: 24,
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <EditOutlined />
                        )
                      }
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  >
                    <Input
                      className="admin-profile-name-input"
                      placeholder="Enter email"
                      disabled
                      prefix={<MailOutlined />}
                      suffix={<EditOutlined />}
                      size="large"
                    />
                  </Form.Item>

                  <div style={{ marginBottom: 16 }}>
                    <Button
                      type="default"
                      size="large"
                      onClick={handleOpenChangeEmail}
                      style={{
                        backgroundColor: "#fff",
                        color: "#000",
                        borderColor: "#d9d9d9",
                      }}
                    >
                      Change Email
                    </Button>
                  </div>
                </Form>

                <div className="admin-profile-tip">
                  <Typography.Text type="secondary">
                    Tip: Upload JPG/PNG/WebP image up to 5MB.
                  </Typography.Text>
                </div>
              </Card>
            </Col>

            {/* <Col xs={24} xl={10}>
              <Card
                bordered
                style={{
                  borderRadius: 14,
                  marginBottom: 16,
                  background:
                    "linear-gradient(135deg, rgba(80,80,80,0.22), rgba(130,130,130,0.18))",
                }}
              >
                <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 17 }}>
                    <span style={{ color: "#565656" }}>Role:</span>
                    <strong>{display?.role ? String(display.role).toUpperCase() : "ADMIN"}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 17 }}>
                    <span style={{ color: "#565656" }}>Verification Date:</span>
                    <strong>{formatLongDate(display?.updatedAt || display?.createdAt)}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 17 }}>
                    <span style={{ color: "#565656" }}>Joined Date:</span>
                    <strong>{formatLongDate(display?.createdAt)}</strong>
                  </div>
                </div>
              </Card>

              <Card bordered style={{ borderRadius: 14, marginBottom: 16 }}>
                <h3 style={{ marginBottom: 14, fontSize: 34 }}>Quick Info</h3>
                <div style={{ display: "grid", gap: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <span style={{ color: "#6f6f6f" }}>Name</span>
                    <strong>{display?.name || "—"}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <span style={{ color: "#6f6f6f" }}>Email</span>
                    <strong style={{ wordBreak: "break-word", textAlign: "right" }}>{display?.email || "—"}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <span style={{ color: "#6f6f6f" }}>Role</span>
                    <strong>{display?.role || "—"}</strong>
                  </div>
                </div>
              </Card>

              <Card bordered style={{ borderRadius: 14 }}>
                <h3 style={{ marginBottom: 12, fontSize: 34 }}>Account Status</h3>
                <Divider style={{ margin: "10px 0 14px" }} />
                <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                  <SafetyCertificateOutlined style={{ color: "#52c41a" }} />
                  <span>
                    Email Status:{" "}
                    <strong>{display?.isEmailVerified ? "Verified" : "Not Verified"}</strong>
                  </span>
                </div>
                <div style={{ color: "#404040", fontWeight: 600, marginBottom: 8 }}>Activity Feed</div>
                <div style={{ display: "grid", gap: 10 }}>
                  {activityFeed.map((line, idx) => (
                    <div key={idx} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <CalendarOutlined style={{ color: "#8c8c8c", marginTop: 3 }} />
                      <div>
                        <div style={{ fontSize: 14 }}>{line}</div>
                        <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                          {formatLongDate(display?.updatedAt || display?.createdAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </Col> */}
          </Row>

          <Modal
            open={deleteConfirmOpen}
            footer={null}
            centered
            closable={false}
            width={520}
            className="profile-image-delete-modal"
            onCancel={() => {
              if (!deletingImage) setDeleteConfirmOpen(false);
            }}
            maskClosable={!deletingImage}
          >
            <button
              type="button"
              className="profile-image-delete-close"
              onClick={() => {
                if (!deletingImage) setDeleteConfirmOpen(false);
              }}
              disabled={deletingImage}
            >
              <CloseOutlined />
            </button>
            <div className="profile-image-delete-icon-wrap">
              <DeleteOutlined className="profile-image-delete-icon" />
            </div>
            <h3 className="profile-image-delete-title">Are you sure you want to delete your profile image?</h3>
            <p className="profile-image-delete-subtitle">
              This action cannot be undone.
            </p>
            <div className="profile-image-delete-actions">
              <Button
                type="primary"
                className="profile-image-delete-cancel"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deletingImage}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                danger
                loading={deletingImage}
                className="profile-image-delete-confirm"
                onClick={confirmDeleteProfileImage}
              >
                Delete
              </Button>
            </div>
          </Modal>

          <Modal
            open={changeEmailOpen}
            title="Change Email"
            onCancel={() => setChangeEmailOpen(false)}
            okText="Send OTP"
            okButtonProps={{
              form: "change-email-form",
              htmlType: "submit",
              loading: emailOtpSending,
            }}
          >
            <Alert
              showIcon
              type="info"
              style={{ marginBottom: 12 }}
              message="Email update requires OTP verification"
              description="Enter your new email. We will send a verification OTP (valid for 10 minutes)."
            />
            <Form
              id="change-email-form"
              form={changeEmailForm}
              layout="vertical"
              onFinish={handleRequestEmailOtp}
            >
              <Form.Item
                label="New Email"
                name="newEmail"
                rules={[
                  { required: true, message: "Please enter new email" },
                  { type: "email", message: "Please enter valid email" },
                ]}
              >
                <Input placeholder="Enter new email address" />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            open={verifyOtpOpen}
            title="Verify New Email OTP"
            onCancel={() => {
              if (!emailOtpVerifying) setVerifyOtpOpen(false);
            }}
            okText="Verify OTP"
            okButtonProps={{
              form: "verify-email-otp-form",
              htmlType: "submit",
              loading: emailOtpVerifying,
            }}
          >
            <Alert
              showIcon
              type="warning"
              style={{ marginBottom: 12 }}
              message={`OTP sent to ${pendingEmail || "new email"}`}
              description="Enter the OTP to verify and update your email."
            />
            <Form
              id="verify-email-otp-form"
              form={verifyOtpForm}
              layout="vertical"
              onFinish={handleVerifyEmailOtp}
            >
              <Form.Item
                label="OTP"
                name="otp"
                rules={[
                  { required: true, message: "Please enter OTP" },
                  { pattern: /^\d{6}$/, message: "OTP must be 6 digits" },
                ]}
              >
                <Input placeholder="Enter OTP" inputMode="numeric" />
              </Form.Item>
            </Form>
            <div style={{ marginTop: 6 }}>
              <Button
                type="link"
                disabled={resendCountdown > 0 || emailOtpSending}
                onClick={handleResendOtp}
                style={{ paddingLeft: 0 }}
              >
                {resendCountdown > 0
                  ? `Resend OTP in ${resendCountdown}s`
                  : "Resend OTP"}
              </Button>
            </div>
          </Modal>
        </>
      )}
    </Card>
  );
};

export default AdminProfile;
